/* ==========================================================================
   Formatação do número da loja e das datas da conexão

   O `displayPhoneNumber` vem como a Meta o exibe ("+55 11 98765-4321") ou só
   com dígitos ("5511987654321", como o laboratório e os webhooks o escrevem).
   O número de "Pedidos no WhatsApp" é gravado só com dígitos, com o 55
   (design D25: a comparação é por dígitos normalizados, e fica no painel).
   ========================================================================== */

const DAY_MS = 86_400_000;

function digits(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '');
}

/** Os dígitos nacionais (DDD + número) de um número brasileiro, sem o 55. */
export function brazilianLocalDigits(value: string | null | undefined): string {
  const all = digits(value);
  return all.startsWith('55') && (all.length === 12 || all.length === 13) ? all.slice(2) : all;
}

/** O mesmo número, comparado pelos dígitos nacionais. Vazio nunca é igual. */
export function sameWhatsappNumber(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = brazilianLocalDigits(a);
  const right = brazilianLocalDigits(b);
  return left !== '' && left === right;
}

/** "(11) 98765-4321" para número brasileiro; o texto original para o resto. */
export function formatWhatsappNumber(value: string | null | undefined): string {
  const local = brazilianLocalDigits(value);
  const isBrazilian = digits(value).startsWith('55') || local.length === 10 || local.length === 11;
  if (isBrazilian && local.length === 11) return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  if (isBrazilian && local.length === 10) return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return value ?? '';
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Dias inteiros desde `iso` até `now`. */
export function daysSince(iso: string, now: number = Date.now()): number {
  return Math.max(0, Math.floor((now - Date.parse(iso)) / DAY_MS));
}

/** "hoje", "ontem", "há 5 dias". */
export function describeDaysAgo(iso: string, now: number = Date.now()): string {
  const days = daysSince(iso, now);
  if (days === 0) return 'hoje';
  if (days === 1) return 'ontem';
  return `há ${days} dias`;
}

/** Qualidade do número na Meta, em linguagem da loja. */
export function describeQuality(rating: string | null): string | null {
  switch (rating?.toUpperCase()) {
    case 'GREEN':
      return 'Alta';
    case 'YELLOW':
      return 'Média';
    case 'RED':
      return 'Baixa';
    default:
      return null;
  }
}
